import type { WebRTCStats } from '@peermetrics/webrtc-stats';

export interface WebRTCAnalyticsManager {
  addConnection: (
    pc: RTCPeerConnection,
    peerId: string,
    callId?: number
  ) => void;
  getAllConnections: () => string[];
  getLatestConnectionMetrics: (
    peerId: string
  ) => Record<string, number | string | undefined> | null;
  getRawWebRTCStats: (peerId: string) => Promise<RTCStatsReport | null>;
  isConnectionActive: (peerId: string) => boolean;
  getActiveConnections: () => string[];
}

// Simplified CallStats interface - only fields used by calculateCallScore and attributeCallQuality
export interface CallStats {
  inboundRtpVideo?: {
    packetsLost?: number;
    packetsReceived?: number;
    framesPerSecond?: number;
    framesDecoded?: number;
    framesDropped?: number;
    framesReceived?: number;
    freezeCount?: number;
    totalFreezesDuration?: number;
    pauseCount?: number;
    totalPausesDuration?: number;
    jitterBufferDelay?: number;
    jitterBufferEmittedCount?: number;
    jitter?: number;
    totalDecodeTime?: number;
  };
  inboundRtpAudio?: {
    packetsLost?: number;
    packetsReceived?: number;
  };
  remoteInboundRtpVideo?: {
    jitter?: number;
    roundTripTime?: number;
    packetsLost?: number;
    fractionLost?: number;
  };
  remoteInboundRtpAudio?: {
    jitter?: number;
    roundTripTime?: number;
  };
  outboundRtpVideo?: {
    qualityLimitationReason?: string;
    qualityLimitationDurations?: Record<string, number>;
    packetsSent?: number;
    /** media-source framesPerSecond: what the local camera delivers to the encoder. */
    sourceFramesPerSecond?: number;
    /** Keyframe requests from the far end; each one means its decoded video broke up. */
    pliCount?: number;
    firCount?: number;
  };
  selectedCandidatePair?: {
    localCandidateType?: string;
    localRelayProtocol?: string;
  };
}

export interface WebRTCStatsData {
  video?: {
    inbound?: {
      packetsLost?: number;
      packetsReceived?: number;
      packetLossRate?: number | null;
      framesPerSecond?: number;
      bitrate?: number;
      frameWidth?: number;
      frameHeight?: number;
      jitter?: number;
      framesDecoded?: number;
      framesDropped?: number;
      framesReceived?: number;
      freezeCount?: number;
      totalFreezesDuration?: number;
      pauseCount?: number;
      totalPausesDuration?: number;
      jitterBufferDelay?: number;
      jitterBufferEmittedCount?: number;
      totalDecodeTime?: number;
    }[];
    outbound?: {
      packetsSent?: number;
      qualityLimitationReason?: string;
      qualityLimitationDurations?: Record<string, number>;
      pliCount?: number;
      firCount?: number;
      frameWidth?: number;
      frameHeight?: number;
      track?: {
        framesPerSecond?: number;
      };
    }[];
  };
  audio?: {
    inbound?: {
      packetsLost?: number;
      packetsReceived?: number;
    }[];
    outbound?: {
      packetsSent?: number;
    }[];
  };
  remote?: {
    video?: {
      inbound?: {
        packetsLost?: number;
        packetsReceived?: number;
        packetLossRate?: number | null;
        fractionLost?: number;
        jitter?: number;
        roundTripTime?: number;
      }[];
    };
    audio?: {
      inbound?: {
        jitter?: number;
        roundTripTime?: number;
      }[];
    };
  };
  connection?: {
    local?: {
      candidateType?: string;
      relayProtocol?: string;
    };
  };
}

export interface WebRTCStatsConfig {
  getStatsInterval: number;
  rawStats: boolean;
  statsObject: boolean;
  filteredStats: boolean;
  remote: boolean;
  wrapGetUserMedia: boolean;
}

export interface RunningMean {
  sum: number;
  count: number;
}

/** Per-call samples of instantaneous stats, so scoring isn't decided by the final poll. */
export interface CallStatsSamples {
  inboundVideoFps: RunningMean;
  inboundVideoJitter: RunningMean;
  sourceVideoFps: RunningMean;
  /** Far-end video jitter at or below 1s. */
  remoteInboundVideoJitter: RunningMean;
  /**
   * Far-end video jitter above 1s. Scored only when no plausible video
   * jitter sample was reported.
   */
  rejectedRemoteVideoJitter: RunningMean;
  remoteInboundVideoRtt: RunningMean;
  /** Far-end audio jitter at or below 1s. */
  remoteInboundAudioJitter: RunningMean;
  /**
   * Far-end audio jitter above 1s. Scored only when no plausible audio
   * jitter sample was reported.
   */
  rejectedRemoteAudioJitter: RunningMean;
  remoteInboundAudioRtt: RunningMean;
}

export interface ConnectionInfo {
  peerId: string;
  pc: RTCPeerConnection;
  webrtcStats: WebRTCStats;
  latestStatsData?: WebRTCStatsData;
  statsSamples?: CallStatsSamples;
  callId?: number;
}

export const DEFAULT_WEBRTC_STATS_CONFIG: WebRTCStatsConfig = {
  getStatsInterval: 5000,
  rawStats: false,
  statsObject: true,
  filteredStats: false,
  remote: true, // Include remote stats for jitter and RTT
  wrapGetUserMedia: false,
};
